import BaseController from "./base"

export default class extends BaseController {
  static targets = ["searchInput", "dropdown", "results", "loading", "selectedArtists", "hiddenInputs"]
  static values = { organizationId: Number }

  connect() {
    super.connect?.()
    
    // Setup outside click handler for dropdown
    this.outsideClickCleanup = this.setupOutsideClickHandler(
      this.dropdownTarget, 
      () => this.hideElement(this.dropdownTarget)
    )
    
    this.hasLoadedInitial = false
    this.selectedArtistIds = new Set()
    
    // Initialize existing artists
    this.initializeExistingArtists()
  }

  disconnect() {
    super.disconnect?.()
    
    if (this.outsideClickCleanup) {
      this.outsideClickCleanup()
    }
  }

  async showDropdown() {
    if (!this.hasLoadedInitial && this.searchInputTarget.value.length === 0) {
      await this.loadInitialArtists()
    } else if (this.searchInputTarget.value.length > 0) {
      this.showElement(this.dropdownTarget)
    } else {
      this.showElement(this.dropdownTarget)
    }
  }

  async loadInitialArtists() {
    this.showLoading()
    this.hasLoadedInitial = true
    
    const { data, success } = await this.fetchData(
      `/albums/search_artists?organization_id=${this.organizationIdValue}&query=&initial=true`
    )
    
    if (success) {
      this.displayResults(data)
      this.showElement(this.dropdownTarget)
    } else {
    }
    
    this.hideLoading()
  }

  async search() {
    const query = this.searchInputTarget.value.trim()
    
    if (query.length < 2) {
      if (this.hasLoadedInitial) {
        this.showElement(this.dropdownTarget)
      } else {
        this.hideElement(this.dropdownTarget)
      }
      return
    }

    this.showLoading()
    
    const { data, success } = await this.fetchData(
      `/albums/search_artists?organization_id=${this.organizationIdValue}&query=${encodeURIComponent(query)}`
    )
    
    if (success) {
      this.displayResults(data)
      this.showElement(this.dropdownTarget)
    } else {
    }
    
    this.hideLoading()
  }

  displayResults(artists) {
    this.displaySearchResults(
      artists,
      this.resultsTarget,
      (artist) => this.selectArtist(artist),
      () => this.showAddArtistModal()
    )
  }

  showAddArtistModal() {
    // Hide dropdown
    this.hideElement(this.dropdownTarget)
    this.searchInputTarget.value = ""

    // Show modal and set the calling controller type
    const modal = document.getElementById("add-artist-modal")
    if (modal) {
      modal.dataset.callingController = this.getCallingControllerType()

      // Set is_contributor flag to true for role-based controllers (contributors)
      const contributorFlag = modal.querySelector('[data-add-artist-form-target="contributorFlag"]')
      if (contributorFlag) {
        const callingController = this.getCallingControllerType()
        const isContributor = callingController && (callingController.startsWith('role_') || callingController.includes('_role_'))
        contributorFlag.value = isContributor ? "true" : "false"
      }

      this.showModal("add-artist-modal")
    }
  }

  selectArtist(artist) {
    // Check if artist is already selected
    if (this.selectedArtistIds.has(artist.id)) {
      return
    }
    
    // Clear search input
    this.searchInputTarget.value = ""
    this.hideElement(this.dropdownTarget)
    
    // Add to selected artists set
    this.selectedArtistIds.add(artist.id)
    
    // Create visual tag and hidden input
    this.addSelectedArtist(artist)
  }

  addSelectedArtist(artist) {
    // Create artist tag with edit and remove handlers
    const { artistTag, editButton, removeButton } = this.createArtistTag(
      artist,
      () => this.editArtist(artist, artist.id, artist.name),
      () => {
        const hiddenInput = document.getElementById(this.getHiddenInputId(artist.id))
        this.removeSelectedArtist(artist.id, artistTag, hiddenInput)
      }
    )
    
    // Create hidden input for form submission
    const hiddenInput = this.createHiddenInput(
      this.getHiddenInputName(),
      artist.id,
      this.getHiddenInputId(artist.id)
    )
    
    // Add to DOM
    this.selectedArtistsTarget.appendChild(artistTag)
    this.getHiddenInputsContainer().appendChild(hiddenInput)
  }

  removeSelectedArtist(artistId, tagElement, hiddenInput) {
    // Remove from selected set
    this.selectedArtistIds.delete(artistId)
    
    // Remove DOM elements
    tagElement.remove()
    if (hiddenInput) {
      hiddenInput.remove()
    }
  }

  async editArtist(artist, artistId, artistName) {

    // Show the edit modal
    const modal = document.getElementById("edit-artist-modal")

    if (modal) {
      // Set is_contributor flag to true for role-based controllers (contributors)
      const contributorFlag = modal.querySelector('[data-edit-artist-form-target="contributorFlag"]')
      if (contributorFlag) {
        const callingController = this.getCallingControllerType()
        const isContributor = callingController && (callingController.startsWith('role_') || callingController.includes('_role_'))
        contributorFlag.value = isContributor ? "true" : "false"
      }

      // Get the edit artist form controller and populate it with artist data
      const editFormElement = modal.querySelector('[data-controller~="edit-artist-form"]')

      if (editFormElement) {
        const editFormController = this.application.getControllerForElementAndIdentifier(editFormElement, 'edit-artist-form')

        if (editFormController) {
          // Fetch the full artist data since we only have basic info
          const fullArtistData = await this.fetchArtistData(artistId)

          editFormController.populateForm(fullArtistData)
          this.showModal("edit-artist-modal")
        } else {
        }
      } else {
      }
    } else {
    }
  }

  async fetchArtistData(artistId) {
    const { data, success } = await this.fetchData(`/artists/${artistId}`)
    
    if (success) {
      return data.artist || data
    } else {
      return { id: artistId, name: 'Unknown Artist' }
    }
  }

  updateArtistTag(updatedArtist) {
    // Find the existing artist tag and update it
    const existingTag = this.selectedArtistsTarget.querySelector(`button[data-artist-id="${updatedArtist.id}"]`)?.closest('.bg-indigo-100')
    
    if (existingTag) {
      // Update the artist name in the tag
      const nameSpan = existingTag.querySelector('span')
      if (nameSpan) {
        nameSpan.textContent = updatedArtist.name
      }
      
      // Update data attributes
      const editButton = existingTag.querySelector('button[data-action="click"]')
      if (editButton) {
        editButton.dataset.artistName = updatedArtist.name
      }
    }
  }

  initializeExistingArtists() {

    // Add existing artist IDs to the selected set to prevent duplicates
    const existingInputs = this.getHiddenInputsContainer().querySelectorAll(`input[name="${this.getHiddenInputName()}"]`)
    existingInputs.forEach(input => {
      if (input.value) {
        this.selectedArtistIds.add(parseInt(input.value))
      }
    })

    // Add event listeners to server-rendered artist tags
    const existingTags = this.selectedArtistsTarget.querySelectorAll('.bg-indigo-100')
    existingTags.forEach(tag => {
      const editButton = tag.querySelector('button[data-action="click"]')
      const removeButton = tag.querySelectorAll('button')[1]


      if (editButton) {
        const artistId = editButton.dataset.artistId
        const artistName = editButton.dataset.artistName
        editButton.addEventListener("click", (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.editArtist({ id: artistId, name: artistName }, artistId, artistName)
        })
      }

      if (removeButton) {
        const artistId = removeButton.dataset.artistId
        const correspondingInput = document.getElementById(this.getHiddenInputId(artistId))
        removeButton.addEventListener("click", () => {
          this.removeSelectedArtist(parseInt(artistId), tag, correspondingInput)
        })
      }
    })
  }

  // Abstract methods to be implemented by subclasses
  getCallingControllerType() {
    throw new Error("getCallingControllerType() must be implemented by subclass")
  }

  getHiddenInputName() {
    throw new Error("getHiddenInputName() must be implemented by subclass")
  }

  getHiddenInputId(artistId) {
    throw new Error("getHiddenInputId() must be implemented by subclass")
  }

  getHiddenInputsContainer() {
    // Default to local target, can be overridden by subclasses
    return this.hiddenInputsTarget
  }
}